import { create } from 'zustand'
import { persist } from 'zustand/middleware'

interface Settings {
  /** Base URL of the Diwa API. In dev, '/api/diwa' is proxied to localhost:8005 by Vite. */
  diwaUrl: string
  /** Gate (universal login) base URL — used for the 401 redirect. */
  gateUrl: string
}

interface UIState extends Settings {
  /** Bearer token from diwa's own /auth/login. Attached by DiwaAPI on every request. */
  token: string | null
  /** An invite token a logged-out visitor clicked, parked here so it survives the
   *  login/registration round-trip (persisted) and can be redeemed afterwards. */
  pendingInvite: string | null
  /** True once the keeper has finished or skipped the first-run onboarding flow. Persisted so
   *  the guided flow never re-traps someone who chose to set things up themselves (a keeper who
   *  creates a domain leaves first-run anyway; this covers the "skip, stay empty" case). */
  onboardingDismissed: boolean
  /** The "Begin here" guided steps now live in the control plane's ◈ tab (persist across pages,
   *  founder ruling). Dismissed = gone for good (like onboardingDismissed); collapsed = folded to
   *  its one-line header but still there. */
  beginHereDismissed: boolean
  beginHereCollapsed: boolean
  updateSettings: (s: Partial<Settings>) => void
  setToken: (t: string | null) => void
  setPendingInvite: (t: string | null) => void
  setOnboardingDismissed: (v: boolean) => void
  setBeginHereDismissed: (v: boolean) => void
  setBeginHereCollapsed: (v: boolean) => void
}

const defaults: Settings = {
  diwaUrl: import.meta.env.VITE_DIWA_API_URL ?? '/api/diwa',
  gateUrl: import.meta.env.VITE_GATE_URL ?? 'http://localhost:8004',
}

export const useUI = create<UIState>()(
  persist(
    (set) => ({
      ...defaults,
      token: null,
      pendingInvite: null,
      onboardingDismissed: false,
      // Migration: Home's old inline card kept its own localStorage flag — honour it so a
      // keeper who already dismissed "Begin here" doesn't meet it again in the rail.
      beginHereDismissed:
        typeof localStorage !== 'undefined' && localStorage.getItem('dw-begin-here') === 'done',
      beginHereCollapsed: false,
      updateSettings: (s) => set(s),
      setToken: (t) => set({ token: t }),
      setPendingInvite: (t) => set({ pendingInvite: t }),
      setOnboardingDismissed: (v) => set({ onboardingDismissed: v }),
      setBeginHereDismissed: (v) => set({ beginHereDismissed: v }),
      setBeginHereCollapsed: (v) => set({ beginHereCollapsed: v }),
    }),
    { name: 'diwa-web-ui' },
  ),
)
